import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { pollsService, PollWithOptions, PollResults } from '../services/polls.service';
import { realitiesService, Reality } from '../services/realities.service';
import { fraudReportsService, FraudReport } from '../services/fraud-reports.service';
import { usePWAInstall } from '../hooks/usePWAInstall';
import { InArticleAd, BannerAd, SmallBannerAd } from '../components/shared/AdSense';
import { FraudBadge } from '../components/polls/FraudBadge';
import { streamEmbedsService, StreamEmbed } from '../services/stream-embeds.service';

// Configuración para mostrar el banner del ganador (cambiar a false para volver a mostrar encuestas)
const SHOW_WINNER_BANNER = false;
// Configuración para mostrar las fotos de los participantes en la encuesta
const SHOW_CONTESTANT_PHOTOS = false;
const SHOW_FACEBOOK_AND_WHATSAPP = false; // Cambiar a true para volver a mostrar los accesos de Facebook y WhatsApp.
const WINNER_INFO = {
  name: 'Alfredo Adame',
  photoUrl: 'https://revistacaucelegal.com/wp-content/uploads/2021/04/alfredo-adame-explica-rsp.jpg',
  show: 'La Granja VIP'
};

export function LandingPage() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { isInstalled, canInstall, installPWA, showInstallPrompt, dismissInstallPrompt } = usePWAInstall();
  const [realities, setRealities] = useState<Reality[]>([]);
  const [selectedRealityId, setSelectedRealityId] = useState<string>('');
  const [polls, setPolls] = useState<PollWithOptions[]>([]);
  const [selectedPoll, setSelectedPoll] = useState<PollWithOptions | null>(null);
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [selectedPoints, setSelectedPoints] = useState<number>(1);
  const [pointsDistribution, setPointsDistribution] = useState<Record<string, number>>({});
  const [availablePoints, setAvailablePoints] = useState<number>(0);
  const [results, setResults] = useState<PollResults[]>([]);
  const [hasVoted, setHasVoted] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [voting, setVoting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [showResults, setShowResults] = useState<boolean>(false);
  const [showVotersModal, setShowVotersModal] = useState<boolean>(false);
  const [selectedOptionForModal, setSelectedOptionForModal] = useState<PollWithOptions['poll_options'][0] | null>(null);
  const [selectedOptionVoters, setSelectedOptionVoters] = useState<PollResults | null>(null);
  const [votersOffset, setVotersOffset] = useState<number>(0);
  const [loadingMoreVoters, setLoadingMoreVoters] = useState<boolean>(false);
  const [hasMoreVoters, setHasMoreVoters] = useState<boolean>(true);
  const [currentOptionIdForVoters, setCurrentOptionIdForVoters] = useState<string | undefined>(undefined);
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [pollExpired, setPollExpired] = useState<boolean>(false);
  const [nextVoteTime, setNextVoteTime] = useState<string>('');
  const [lastVoteTimestamp, setLastVoteTimestamp] = useState<string | null>(null);
  const [hasShared, setHasShared] = useState<boolean>(false);
  const [sharing, setSharing] = useState<boolean>(false);
  const [checkingVote, setCheckingVote] = useState<boolean>(false);
  const lastCheckedRef = useRef<string>('');
  const pollsLoadedRef = useRef<boolean>(false);
  const votersListRef = useRef<HTMLDivElement>(null);
  const [fraudReports, setFraudReports] = useState<Record<string, FraudReport[]>>({});
  const [activeTab, setActiveTab] = useState<'encuesta' | 'votacion-oficial'>('encuesta');
  const [streamEmbeds, setStreamEmbeds] = useState<StreamEmbed[]>([]);
  const [activeStreamId, setActiveStreamId] = useState<string>('');

  // Helper functions for distribution mode
  const getTotalAssignedPoints = (): number => {
    return Object.values(pointsDistribution).reduce((sum, points) => sum + points, 0);
  };

  const getRemainingPoints = (): number => {
    return availablePoints - getTotalAssignedPoints();
  };

  const isDistributionMode = (selectedPoll?.poll_points_config?.max_points_per_user ?? 1) > 1;

  // Helper function to get relative time
  const getRelativeTime = (timestamp: string): string => {
    const now = new Date();
    const past = new Date(timestamp);
    const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return `hace ${diffInSeconds} segundo${diffInSeconds !== 1 ? 's' : ''}`;
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `hace ${diffInMinutes} minuto${diffInMinutes !== 1 ? 's' : ''}`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `hace ${diffInHours} hora${diffInHours !== 1 ? 's' : ''}`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
      return `hace ${diffInDays} día${diffInDays !== 1 ? 's' : ''}`;
    }

    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return `hace ${diffInMonths} mes${diffInMonths !== 1 ? 'es' : ''}`;
    }

    const diffInYears = Math.floor(diffInMonths / 12);
    return `hace ${diffInYears} año${diffInYears !== 1 ? 's' : ''}`;
  };

  // No redirigir automáticamente a los admins, permitirles ver la landing page

  useEffect(() => {
    if (!pollsLoadedRef.current) {
      pollsLoadedRef.current = true;
      loadRealitiesAndPolls();
    }
    streamEmbedsService.getActive().then((embeds) => {
      setStreamEmbeds(embeds);
      if (embeds.length > 0) setActiveStreamId(embeds[0].id);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cuando cambia el reality seleccionado, actualizar la encuesta seleccionada
  useEffect(() => {
    if (selectedRealityId && polls.length > 0) {
      const pollsFromReality = getFilteredPolls();
      if (pollsFromReality.length > 0) {
        const featuredPoll = pollsFromReality.find(poll => poll.is_featured);
        setSelectedPoll(featuredPoll || pollsFromReality[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRealityId, polls]);

  useEffect(() => {
    const key = `${selectedPoll?.id}-${user?.id}`;
    if (selectedPoll && user && lastCheckedRef.current !== key) {
      lastCheckedRef.current = key;
      checkIfVoted();
      loadResults();
    } else if (selectedPoll && !user) {
      // Si no hay usuario pero hay encuesta, mostrar resultados
      setShowResults(true);
      loadResults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPoll, user]);

  // Countdown timer effect
  useEffect(() => {
    if (!selectedPoll?.end_date) {
      setTimeRemaining('');
      setPollExpired(false);
      return;
    }

    const updateCountdown = () => {
      const now = new Date().getTime();
      const endTime = new Date(selectedPoll.end_date!).getTime();
      const distance = endTime - now;

      if (distance < 0) {
        setTimeRemaining('');
        setPollExpired(true);
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      if (days > 0) {
        setTimeRemaining(`${days}d ${hours}h ${minutes}m ${seconds}s`);
      } else if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m ${seconds}s`);
      } else if (minutes > 0) {
        setTimeRemaining(`${minutes}m ${seconds}s`);
      } else {
        setTimeRemaining(`${seconds}s`);
      }

      setPollExpired(false);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [selectedPoll?.end_date]);

  // Countdown for next vote (2-hour cooldown from last vote)
  useEffect(() => {
    const shouldShowCountdown =
      selectedPoll?.poll_points_config?.daily_reset &&
      (isDistributionMode ? availablePoints === 0 : hasVoted) &&
      lastVoteTimestamp;

    if (!shouldShowCountdown) {
      setNextVoteTime('');
      return;
    }

    const updateNextVoteCountdown = () => {
      const now = new Date();
      const lastVote = new Date(lastVoteTimestamp);
      const twoHoursFromLastVote = new Date(lastVote.getTime() + 2 * 60 * 60 * 1000);

      const diff = twoHoursFromLastVote.getTime() - now.getTime();

      if (diff <= 0) {
        setNextVoteTime('00:00:00');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setNextVoteTime(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
    };

    updateNextVoteCountdown();
    const interval = setInterval(updateNextVoteCountdown, 1000);

    return () => clearInterval(interval);
  }, [hasVoted, selectedPoll?.poll_points_config?.daily_reset, isDistributionMode, availablePoints, lastVoteTimestamp]);

  const loadRealitiesAndPolls = async () => {
    try {
      setLoading(true);

      // Cargar realities activos (ya ordenados por display_order)
      const realitiesData = await realitiesService.getAll();
      const activeRealities = realitiesData.filter(r => r.active === true);
      setRealities(activeRealities);

      // Cargar todas las encuestas activas
      const pollsData = await pollsService.getActivePolls();
      setPolls(pollsData);

      // Seleccionar el primer reality que tenga una encuesta featured.
      // Si ninguno tiene una principal, usar el primero que tenga encuestas.
      if (activeRealities.length > 0) {
        const firstRealityWithFeatured = activeRealities.find(reality => {
          return pollsData.some(poll =>
            poll.seasons?.reality_id === reality.id && poll.is_featured
          );
        });

        const firstRealityWithPolls = activeRealities.find(reality =>
          pollsData.some(poll => poll.seasons?.reality_id === reality.id)
        );

        const initialReality = firstRealityWithFeatured || firstRealityWithPolls;
        if (initialReality) {
          setSelectedRealityId(initialReality.id);
        }
      }

      // La selección de encuesta se hará en el useEffect cuando cambien selectedRealityId y polls
    } catch (err: any) {
      setError(err.message || 'Error loading data');
    } finally {
      setLoading(false);
    }
  };

  // Filtrar encuestas por el reality seleccionado
  const getFilteredPolls = (): PollWithOptions[] => {
    if (!selectedRealityId) return [];
    return polls.filter(poll => poll.seasons?.reality_id === selectedRealityId);
  };

  // Obtener la encuesta featured de un reality
  const getFeaturedPoll = (realityId: string): PollWithOptions | undefined => {
    return polls.find(poll => poll.seasons?.reality_id === realityId && poll.is_featured);
  };

  // Filtrar realities que tienen al menos una encuesta (featured o no)
  // TEMPORALMENTE MODIFICADO: Acepta cualquier encuesta, no solo featured
  const getActiveRealitiesWithPolls = (): Reality[] => {
    return realities.filter(reality => {
      // Buscar cualquier encuesta para este reality
      const hasPolls = polls.some(poll => poll.seasons?.reality_id === reality.id);
      return hasPolls;
    });
  };

  const getOfficialVotingConfig = () => {
    const realityName = realities.find(reality => reality.id === selectedRealityId)?.name.toLowerCase() || '';

    if (realityName.includes('granja')) {
      return {
        title: 'Votación oficial de La Granja VIP',
        embedUrl: 'https://vota.tvazteca.com/aztecauno/la-granja-vip/vota/',
        officialUrl: 'https://www.tvazteca.com/aztecauno/la-granja-vip/vota/',
        cropToVotingBlock: true
      };
    }

    if (realityName.includes('casa')) {
      return {
        title: 'Votación oficial de La Casa de los Famosos México',
        embedUrl: 'https://mega.page/67bc83bd1d09c96255e3de68',
        officialUrl: 'https://www.lacasadelosfamososmexico.tv/vota/',
        cropToVotingBlock: false
      };
    }

    return null;
  };

  const checkIfVoted = async () => {
    if (!selectedPoll || !user) return;
    try {
      setCheckingVote(true);
      const dailyReset = selectedPoll.poll_points_config?.daily_reset || false;

      let voted = false;
      if (dailyReset) {
        // Check if voted today for daily reset mode
        voted = await pollsService.hasUserVotedToday(selectedPoll.id, user.id);
      } else {
        // Check if voted ever for permanent vote mode
        voted = await pollsService.hasUserVoted(selectedPoll.id, user.id);
      }

      setHasVoted(voted);
      // Mostrar resultados por defecto cuando estás logueado
      setShowResults(true);

      // Get last vote timestamp (for 2-hour cooldown countdown)
      if (dailyReset && voted) {
        const lastVote = await pollsService.getLastVoteTime(selectedPoll.id, user.id);
        setLastVoteTimestamp(lastVote);
      }

      // Check if user has shared today (only relevant for daily_reset mode)
      if (dailyReset) {
        const shared = await pollsService.hasSharedToday(selectedPoll.id, user.id);
        setHasShared(shared);
      }

      // Cargar puntos disponibles si es modo distribución
      const isDistMode = (selectedPoll.poll_points_config?.max_points_per_user ?? 1) > 1;
      if (isDistMode) {
        // Obtener puntos disponibles (considera daily_reset automáticamente)
        const points = await pollsService.getAvailablePoints(selectedPoll.id, user.id);
        setAvailablePoints(points);

        // Inicializar distribución vacía (cada voto es nuevo, no modificamos anteriores)
        setPointsDistribution({});
      } else {
        // Resetear estado de voto único
        setSelectedOption('');
      }
    } catch (err) {
    } finally {
      setCheckingVote(false);
    }
  };

  const loadResults = async () => {
    if (!selectedPoll) return;
    try {
      const data = await pollsService.getPollResults(selectedPoll.id);
      setResults(data);

      // Load fraud reports for this poll
      const reports = await fraudReportsService.getByPoll(selectedPoll.id);
      const reportsByOption: Record<string, FraudReport[]> = {};
      reports.forEach(report => {
        if (!reportsByOption[report.poll_option_id]) {
          reportsByOption[report.poll_option_id] = [];
        }
        reportsByOption[report.poll_option_id].push(report);
      });
      setFraudReports(reportsByOption);
    } catch (err) {
    }
  };

  const handleVote = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      setVoting(true);
      setError('');

      if (isDistributionMode) {
        // Modo distribución de puntos
        const totalAssigned = getTotalAssignedPoints();

        if (totalAssigned === 0) {
          setError('Por favor asigna al menos 1 punto');
          return;
        }

        if (totalAssigned > availablePoints) {
          setError(`No puedes asignar más de ${availablePoints} puntos`);
          return;
        }

        // Preparar distribución de votos
        const distribution = Object.entries(pointsDistribution)
          .filter(([_, points]) => points > 0)
          .map(([optionId, points]) => ({ optionId, points }));

        await pollsService.voteDistribution(selectedPoll!.id, distribution);
      } else {
        // Modo voto único
        if (!selectedOption) {
          setError('Por favor selecciona una opción');
          return;
        }

        await pollsService.vote(selectedPoll!.id, selectedOption, 1);
      }

      setHasVoted(true);
      setShowResults(true);
      await loadResults();

      // Set timestamp for countdown (2-hour cooldown)
      if (selectedPoll?.poll_points_config?.daily_reset) {
        setLastVoteTimestamp(new Date().toISOString());
      }

      // Recalcular puntos disponibles después de votar (para modo distribución)
      if (isDistributionMode && user && selectedPoll) {
        const updatedPoints = await pollsService.getAvailablePoints(selectedPoll.id, user.id);
        setAvailablePoints(updatedPoints);
        setPointsDistribution({}); // Limpiar distribución
      }
    } catch (err: any) {
      setError(err.message || 'Error al votar');
    } finally {
      setVoting(false);
    }
  };

  const handleSignIn = () => {
    navigate('/login');
  };

  const handleShare = async (platform: 'facebook' | 'twitter' | 'whatsapp' | 'telegram' | 'copy_link') => {
    if (!user || !selectedPoll) {
      navigate('/login');
      return;
    }

    try {
      setSharing(true);
      setError('');

      // Register share in backend
      await pollsService.sharePoll(selectedPoll.id, platform as any);

      // Update states
      setHasShared(true);

      // Reload available points (should now include bonus)
      const points = await pollsService.getAvailablePoints(selectedPoll.id, user.id);
      setAvailablePoints(points);

      // Open share dialog based on platform
      const shareUrl = 'https://lacasavota.com';
      const shareText = `¡Vota en ${selectedPoll.title}! 🐔🐄🐷 ${shareUrl}`;

      switch (platform) {
        case 'facebook':
          window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
          break;
        case 'twitter':
          window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`, '_blank');
          break;
        case 'whatsapp':
          window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank');
          break;
        case 'telegram':
          window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(`¡Vota en ${selectedPoll.title}! 🐔🐄🐷`)}`, '_blank');
          break;
        case 'copy_link':
          await navigator.clipboard.writeText(shareText);
          alert('¡Enlace copiado al portapapeles!');
          break;
      }
    } catch (err: any) {
      setError(err.message || 'Error al compartir');
    } finally {
      setSharing(false);
    }
  };

  const getOptionPercentage = (optionId: string): number => {
    const totalPoints = results.reduce((sum, r) => sum + r.total_points, 0);
    const optionResult = results.find(r => r.option_id === optionId);
    return totalPoints > 0 ? ((optionResult?.total_points || 0) / totalPoints) * 100 : 0;
  };

  const handleShowVoters = async (option: PollWithOptions['poll_options'][0]) => {
    if (!selectedPoll) return;

    // Reset pagination state
    setVotersOffset(0);
    setHasMoreVoters(true);
    setCurrentOptionIdForVoters(option.id);

    // Mostrar modal con loading
    setSelectedOptionForModal(option);
    setSelectedOptionVoters(null);
    setShowVotersModal(true);

    try {
      // Cargar votantes bajo demanda
      const voters = await pollsService.getOptionVoters(selectedPoll.id, option.id, 50, 0);
      const optionResult = results.find(r => r.option_id === option.id);

      setSelectedOptionVoters({
        option_id: option.id,
        total_votes: optionResult?.total_votes || 0,
        total_points: optionResult?.total_points || 0,
        voters,
      });

      // Si recibimos menos de 50, no hay más
      setHasMoreVoters(voters.length >= 50);
      setVotersOffset(50);
    } catch (err) {
    }
  };

  const handleShowAllVoters = async () => {
    if (!selectedPoll) return;

    // Reset pagination state
    setVotersOffset(0);
    setHasMoreVoters(true);
    setCurrentOptionIdForVoters(undefined);

    // Mostrar modal con loading
    setSelectedOptionForModal(null);
    setSelectedOptionVoters(null);
    setShowVotersModal(true);

    try {
      // Cargar todos los votantes bajo demanda (sin filtro de option_id)
      const voters = await pollsService.getOptionVoters(selectedPoll.id, undefined, 50, 0);

      // Agregar nombre de opción a cada votante
      const votersWithOptionName = voters.map(v => ({
        ...v,
        option_name: selectedPoll.poll_options.find(opt => opt.id === v.option_id)?.contestants?.full_name ||
                     selectedPoll.poll_options.find(opt => opt.id === v.option_id)?.text || '',
      }));

      setSelectedOptionVoters({
        option_id: 'all',
        total_votes: results.reduce((sum, r) => sum + r.total_votes, 0),
        total_points: results.reduce((sum, r) => sum + r.total_points, 0),
        voters: votersWithOptionName as any, // as any because we added option_name
      });

      // Si recibimos menos de 50, no hay más
      setHasMoreVoters(voters.length >= 50);
      setVotersOffset(50);
    } catch (err) {
    }
  };

  const loadMoreVoters = async () => {
    if (!selectedPoll || !hasMoreVoters || loadingMoreVoters || !selectedOptionVoters) return;

    setLoadingMoreVoters(true);

    try {
      const voters = await pollsService.getOptionVoters(
        selectedPoll.id,
        currentOptionIdForVoters,
        50,
        votersOffset
      );

      if (currentOptionIdForVoters === undefined) {
        // All voters mode - add option names
        const votersWithOptionName = voters.map(v => ({
          ...v,
          option_name: selectedPoll.poll_options.find(opt => opt.id === v.option_id)?.contestants?.full_name ||
                       selectedPoll.poll_options.find(opt => opt.id === v.option_id)?.text || '',
        }));

        setSelectedOptionVoters(prev => ({
          ...prev!,
          voters: [...prev!.voters, ...votersWithOptionName as any],
        }));
      } else {
        // Single option mode
        setSelectedOptionVoters(prev => ({
          ...prev!,
          voters: [...prev!.voters, ...voters],
        }));
      }

      // Si recibimos menos de 50, no hay más
      setHasMoreVoters(voters.length >= 50);
      setVotersOffset(prev => prev + voters.length);
    } catch (err) {
    } finally {
      setLoadingMoreVoters(false);
    }
  };

  // Infinite scroll handler
  const handleVotersScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const bottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 50; // 50px threshold

    if (bottom && hasMoreVoters && !loadingMoreVoters) {
      loadMoreVoters();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-violet-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando encuestas...</p>
        </div>
      </div>
    );
  }

  // Obtener realities que tienen encuestas featured
  const activeRealitiesWithPolls = getActiveRealitiesWithPolls();
  const officialVoting = getOfficialVotingConfig();

  // TEMPORALMENTE DESACTIVADO: Validación de encuestas fijadas
  if (polls.length === 0 /* || activeRealitiesWithPolls.length === 0 */) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-violet-50 to-white flex items-center justify-center">
        <div className="text-center px-4">
          <h1 className="text-2xl font-bold text-gray-800">No hay encuestas activas</h1>
          <p className="mt-2 text-gray-600">Vuelve pronto para votar</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-white dark:bg-gradient-to-br dark:from-gray-900 dark:via-gray-800 dark:to-black bg-gradient-to-br from-violet-50 to-white ${user ? 'pb-20 md:pb-0 md:pt-16' : ''}`}>
      {/* Header - Solo visible si no hay usuario */}
      {!user && (
        <header className="bg-violet-600 dark:bg-gray-800 text-white shadow-lg border-b border-violet-700 dark:border-gray-700">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl md:text-3xl font-bold">La Granja Vota</h1>
              <button
                onClick={handleSignIn}
                className="bg-white text-violet-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition"
              >
                Iniciar Sesión
              </button>
            </div>
          </div>
        </header>
      )}

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 max-w-2xl">
        {/* In-Article Ad */}
        <InArticleAd className="mb-6" />

        {/* Winner Banner - Shows when SHOW_WINNER_BANNER is true */}
        {SHOW_WINNER_BANNER ? (
          <div className="space-y-6">
            {/* Winner Announcement Card */}
            <div className="bg-gradient-to-br from-yellow-400 via-yellow-500 to-orange-500 rounded-2xl shadow-2xl p-1">
              <div className="bg-white dark:bg-gray-900 rounded-xl p-6 md:p-8">
                {/* Trophy Icon */}
                <div className="text-center mb-6">
                  <div className="inline-block text-6xl md:text-8xl animate-bounce">
                    🏆
                  </div>
                </div>

                {/* Winner Title */}
                <h1 className="text-2xl md:text-4xl font-extrabold text-center text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-orange-600 mb-6">
                  ¡TENEMOS GANADOR!
                </h1>

                {/* Winner Photo */}
                <div className="flex justify-center mb-6">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full blur-lg opacity-50 animate-pulse"></div>
                    <img
                      src={WINNER_INFO.photoUrl}
                      alt={WINNER_INFO.name}
                      className="relative w-40 h-40 md:w-56 md:h-56 rounded-full object-cover border-4 border-yellow-400 shadow-xl"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/logo192.png';
                      }}
                    />
                    <div className="absolute -bottom-2 -right-2 text-4xl md:text-5xl">
                      👑
                    </div>
                  </div>
                </div>

                {/* Winner Name */}
                <h2 className="text-3xl md:text-5xl font-black text-center text-gray-900 dark:text-white mb-2">
                  {WINNER_INFO.name}
                </h2>
                <p className="text-lg md:text-xl text-center text-gray-600 dark:text-gray-400 mb-6">
                  Ganador de {WINNER_INFO.show}
                </p>

                {/* Congratulations Message */}
                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-xl p-6 mb-6">
                  <p className="text-center text-lg md:text-xl text-gray-800 dark:text-gray-200 font-medium">
                    ¡Felicidades Alfredo por este gran triunfo!
                  </p>
                </div>

                {/* Divider */}
                <div className="flex items-center gap-4 my-8">
                  <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent"></div>
                  <span className="text-2xl">⭐</span>
                  <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent"></div>
                </div>

                {/* Coming Soon - La Casa de los Famosos */}
                <div className="text-center mb-6">
                  <p className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-3">
                    ¡Nos vemos próximamente!
                  </p>
                  <div className="inline-block bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white px-6 py-3 rounded-full font-bold text-lg md:text-xl shadow-lg">
                    La Casa de los Famosos - Telemundo
                  </div>
                </div>

                {/* Thank You Message */}
                <div className="bg-violet-50 dark:bg-violet-900/20 rounded-xl p-6 mt-6">
                  <p className="text-center text-gray-700 dark:text-gray-300 text-lg">
                    <span className="font-bold text-violet-600 dark:text-violet-400">¡Gracias a todos por participar!</span>
                    <br />
                    <span className="text-base">Su apoyo y entusiasmo hicieron de esta temporada algo especial.</span>
                  </p>
                </div>

                {/* Social sharing hint */}
                <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
                  Síguenos para más contenido y actualizaciones
                </p>
              </div>
            </div>
          </div>
        ) : (
          <>
        {/* Reality Tabs */}
        {activeRealitiesWithPolls.length > 0 && (
          <div className="mb-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-2">
              <div className="flex gap-2 overflow-x-auto">
                {activeRealitiesWithPolls.map((reality) => (
                  <button
                    key={reality.id}
                    onClick={() => setSelectedRealityId(reality.id)}
                    className={`flex-1 min-w-[120px] px-4 py-3 rounded-lg font-semibold transition-all ${
                      selectedRealityId === reality.id
                        ? 'bg-violet-600 text-white shadow-md'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    <div className="text-sm md:text-base">{reality.name}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Subtabs de encuesta */}
        <div>
          <div className="bg-white dark:bg-gray-800 rounded-t-lg shadow-lg p-2 pb-0">
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('encuesta')}
                className={`flex-1 px-4 py-3 rounded-t-lg font-semibold transition-all ${
                  activeTab === 'encuesta'
                    ? 'bg-violet-600 text-white shadow-md'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <div className="text-sm md:text-base">Encuesta</div>
              </button>
              <button
                onClick={() => setActiveTab('votacion-oficial')}
                className={`flex-1 px-4 py-3 rounded-t-lg font-semibold transition-all ${
                  activeTab === 'votacion-oficial'
                    ? 'bg-violet-600 text-white shadow-md'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <div className="text-sm md:text-base">Votación oficial</div>
              </button>
            </div>
          </div>
        </div>

        {activeTab === 'votacion-oficial' && (
          <div className="bg-white dark:bg-gray-800 rounded-b-lg shadow-lg overflow-hidden mb-6 border-t border-gray-200 dark:border-gray-700">
            {officialVoting ? (
              <>
                <div className="bg-violet-50 dark:bg-violet-900/20 border-l-4 border-violet-600 dark:border-violet-400 p-4">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <span className="font-bold">ℹ️ {officialVoting.title}.</span> Estos votos sí cuentan para el programa y son administrados por el sitio oficial.
                  </p>
                </div>
                {officialVoting.cropToVotingBlock ? (
                  <div className="relative h-[640px] overflow-hidden bg-white">
                    <iframe
                      src={officialVoting.embedUrl}
                      title={officialVoting.title}
                      className="absolute left-0 top-0 w-full h-[1600px] border-0 -translate-y-[630px] sm:-translate-y-[720px]"
                      loading="lazy"
                      allow="camera *; microphone *; fullscreen"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <iframe
                    src={officialVoting.embedUrl}
                    title={officialVoting.title}
                    className="w-full min-h-[1100px] border-0"
                    loading="lazy"
                    allow="camera *; microphone *; fullscreen"
                    allowFullScreen
                  />
                )}
                <div className="p-4 text-center border-t border-gray-200 dark:border-gray-700">
                  <a
                    href={officialVoting.officialUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center bg-violet-600 hover:bg-violet-700 text-white font-semibold py-2.5 px-4 rounded-lg transition"
                  >
                    Abrir votación en el sitio oficial
                  </a>
                </div>
              </>
            ) : (
              <div className="p-6 text-center text-gray-600 dark:text-gray-300">
                No hay una votación oficial disponible para este reality.
              </div>
            )}
          </div>
        )}

        {/* Poll Content - Only show when encuesta tab is active */}
        {activeTab === 'encuesta' && selectedPoll && (
          <div className="bg-white dark:bg-gray-800 rounded-b-lg shadow-lg p-6 mb-6 border-t border-gray-200 dark:border-gray-700">
            {/* Back Button - Only in voting mode */}
            {!showResults && selectedPoll.status === 'active' && !pollExpired && (
              <button
                onClick={() => setShowResults(true)}
                className="text-left text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 font-medium transition mb-4"
              >
                &lt; VOLVER
              </button>
            )}

            <div className="flex items-center justify-between mb-2">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {selectedPoll.title}
              </h2>
              {(selectedPoll.status === 'closed' || pollExpired) && (
                <span className="bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 text-xs font-semibold px-3 py-1 rounded-full">
                  Cerrada
                </span>
              )}
            </div>
            {selectedPoll.description && (
              <p className="text-gray-600 dark:text-gray-300 mb-4">{selectedPoll.description}</p>
            )}

            {/* Countdown Timer */}
            {timeRemaining && selectedPoll.status === 'active' && !pollExpired && (
              <div className="bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-lg p-3 mb-4">
                <p className="text-sm font-semibold text-violet-800 dark:text-violet-300 text-center">
                  ⏰ Tiempo restante: {timeRemaining}
                </p>
              </div>
            )}

            {(selectedPoll.status === 'closed' || pollExpired) && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mb-4">
                <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-300 text-center">
                  🔒 VOTACIONES CERRADAS
                </p>
              </div>
            )}

            {/* Voting Section */}
            {!showResults && selectedPoll.status === 'active' && !pollExpired ? (
              <div className="space-y-4">
                {/* Distribution Mode */}
                {isDistributionMode ? (
                  <>
                    {/* Checking Vote State */}
                    {user && checkingVote ? (
                      <div className="rounded-lg p-4 mb-4 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600">
                        <div className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-600"></div>
                          <p className="text-sm text-gray-600 dark:text-gray-300">Verificando...</p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className={`rounded-lg p-4 mb-4 ${
                          user && availablePoints === 0
                            ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
                            : 'bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800'
                        }`}>
                          {user && availablePoints === 0 ? (
                        selectedPoll.poll_points_config?.daily_reset ? (
                          <div className="text-center">
                            <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-300 mb-1">
                              Ya has usado todos tus puntos hoy
                            </p>
                            <p className="text-xs text-yellow-700 dark:text-yellow-400 mb-3">
                              Recibirás más puntos en {nextVoteTime}
                            </p>

                            {/* Share Section - Only show if user hasn't shared today */}
                            {!hasShared && (
                              <div className="mt-3 pt-3 border-t border-yellow-300 dark:border-yellow-700">
                                <p className="text-sm font-bold text-yellow-900 dark:text-yellow-200 mb-2">
                                  🎁 ¡Comparte y obtén {selectedPoll.poll_points_config?.daily_share_bonus || 10} puntos extra!
                                </p>
                                <div className="flex gap-2 justify-center">
                                  {SHOW_FACEBOOK_AND_WHATSAPP && (
                                  <button
                                    onClick={() => handleShare('facebook')}
                                    disabled={sharing}
                                    className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg transition disabled:opacity-50"
                                    title="Compartir en Facebook"
                                  >
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                    </svg>
                                  </button>
                                  )}
                                  <button
                                    onClick={() => handleShare('twitter')}
                                    disabled={sharing}
                                    className="bg-black hover:bg-gray-800 text-white p-2 rounded-lg transition disabled:opacity-50"
                                    title="Compartir en X"
                                  >
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                    </svg>
                                  </button>
                                  {SHOW_FACEBOOK_AND_WHATSAPP && (
                                  <button
                                    onClick={() => handleShare('whatsapp')}
                                    disabled={sharing}
                                    className="bg-green-600 hover:bg-green-700 text-white p-2 rounded-lg transition disabled:opacity-50"
                                    title="Compartir en WhatsApp"
                                  >
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                                    </svg>
                                  </button>
                                  )}
                                  <button
                                    onClick={() => handleShare('telegram')}
                                    disabled={sharing}
                                    className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-lg transition disabled:opacity-50"
                                    title="Compartir en Telegram"
                                  >
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z"/>
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-300 text-center">
                            Ya has usado todos tus puntos
                          </p>
                        )
                      ) : (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                              💰 Puntos restantes:
                            </span>
                            <span className="text-2xl font-bold text-violet-600 dark:text-violet-400">
                              {getRemainingPoints()} de {availablePoints}
                            </span>
                          </div>
                          {selectedPoll.poll_points_config?.daily_reset && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                              ⏰ Tus puntos se resetean diariamente
                            </p>
                          )}
                        </>
                      )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">
                            Distribuye tus puntos:
                          </label>
                      <div className="space-y-3">
                        {selectedPoll.poll_options.map(option => {
                          const points = pointsDistribution[option.id] || 0;
                          const isSaved = option.is_saved || false;
                          const canIncrease = getRemainingPoints() > 0 && !isSaved;
                          const canDecrease = points > 0 && !isSaved;

                          return (
                            <div
                              key={option.id}
                              className={`rounded-lg p-4 border ${
                                isSaved
                                  ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-700'
                                  : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                {SHOW_CONTESTANT_PHOTOS && option.contestants?.photo_url && (
                                  <img
                                    src={option.contestants.photo_url}
                                    alt={option.contestants?.full_name || option.text || ''}
                                    className={`w-12 h-12 rounded-full object-cover flex-shrink-0 ${
                                      isSaved ? 'opacity-60' : ''
                                    }`}
                                  />
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className={`font-medium block truncate ${
                                      isSaved
                                        ? 'text-emerald-800 dark:text-emerald-300'
                                        : 'text-gray-800 dark:text-white'
                                    }`}>
                                      {option.contestants?.full_name || option.text}
                                    </span>
                                    {isSaved && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 dark:bg-emerald-800 text-emerald-800 dark:text-emerald-200 whitespace-nowrap">
                                        ✓ Salvado
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => {
                                      setPointsDistribution(prev => ({
                                        ...prev,
                                        [option.id]: Math.max(0, (prev[option.id] || 0) - 1)
                                      }));
                                    }}
                                    disabled={!canDecrease}
                                    className="w-8 h-8 flex items-center justify-center bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg font-bold text-gray-700 dark:text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-500 transition"
                                  >
                                    −
                                  </button>
                                  <span className="w-10 text-center font-bold text-lg text-gray-900 dark:text-white">
                                    {points}
                                  </span>
                                  <button
                                    onClick={() => {
                                      setPointsDistribution(prev => ({
                                        ...prev,
                                        [option.id]: (prev[option.id] || 0) + 1
                                      }));
                                    }}
                                    disabled={!canIncrease}
                                    className="w-8 h-8 flex items-center justify-center bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg font-bold text-gray-700 dark:text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-500 transition"
                                  >
                                    +
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {getTotalAssignedPoints() > 0 && (
                        <button
                          onClick={() => setPointsDistribution({})}
                          className="mt-3 text-sm text-red-600 dark:text-red-400 hover:underline"
                        >
                          Limpiar distribución
                        </button>
                      )}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  /* Single Vote Mode */
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">
                      Selecciona tu opción:
                    </label>
                    <div className="space-y-2">
                      {selectedPoll.poll_options.map(option => {
                        const isSaved = option.is_saved || false;
                        return (
                          <button
                            key={option.id}
                            onClick={() => !isSaved && setSelectedOption(option.id)}
                            disabled={isSaved}
                            className={`w-full p-4 rounded-lg border-2 transition text-left ${
                              isSaved
                                ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-700 cursor-not-allowed opacity-75'
                                : selectedOption === option.id
                                ? 'border-violet-600 bg-violet-50 dark:bg-violet-900/30'
                                : 'border-gray-300 dark:border-gray-600 hover:border-violet-400 dark:hover:border-violet-500'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              {SHOW_CONTESTANT_PHOTOS && option.contestants?.photo_url && (
                                <img
                                  src={option.contestants.photo_url}
                                  alt={option.contestants?.full_name || option.text || ''}
                                  className={`w-12 h-12 rounded-full object-cover ${
                                    isSaved ? 'opacity-60' : ''
                                  }`}
                                />
                              )}
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className={`font-medium ${
                                    isSaved
                                      ? 'text-emerald-800 dark:text-emerald-300'
                                      : 'text-gray-800 dark:text-white'
                                  }`}>
                                    {option.contestants?.full_name || option.text}
                                  </span>
                                  {isSaved && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 dark:bg-emerald-800 text-emerald-800 dark:text-emerald-200 whitespace-nowrap">
                                      ✓ Salvado
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {error}
                  </div>
                )}

                {/* Botón de votar o mensaje cuando ya votó */}
                {user && checkingVote ? (
                  <div className="rounded-lg p-4 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-600"></div>
                      <p className="text-sm text-gray-600 dark:text-gray-300">Verificando...</p>
                    </div>
                  </div>
                ) : hasVoted && !isDistributionMode ? (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                    {selectedPoll.poll_points_config?.daily_reset ? (
                      <div className="text-center">
                        <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-300 mb-1">
                          Ya has votado hoy
                        </p>
                        <p className="text-xs text-yellow-700 dark:text-yellow-400">
                          Podrás votar de nuevo en {nextVoteTime}
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-300 text-center">
                        Ya has votado en esta encuesta
                      </p>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={handleVote}
                    disabled={
                      voting ||
                      (isDistributionMode ? getTotalAssignedPoints() === 0 || availablePoints === 0 : (hasVoted || !selectedOption))
                    }
                    className="w-full bg-violet-600 text-white py-4 rounded-lg font-bold text-lg hover:bg-violet-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {voting ? 'Votando...' : 'Votar'}
                  </button>
                )}

                <button
                  onClick={() => setShowResults(true)}
                  className="w-full text-violet-600 py-2 text-sm hover:underline"
                >
                  Ver resultados
                </button>
              </div>
            ) : (
              /* Results Section */
              <div className="space-y-4">
                {selectedPoll.status === 'active' && !pollExpired && (
                  user && checkingVote ? (
                    <div className="rounded-lg p-4 mb-4 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600">
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-600"></div>
                        <p className="text-sm text-gray-600 dark:text-gray-300">Verificando...</p>
                      </div>
                    </div>
                  ) : user && ((hasVoted && !isDistributionMode) || (isDistributionMode && availablePoints === 0)) ? (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
                      {selectedPoll.poll_points_config?.daily_reset ? (
                        <div className="text-center">
                          <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-300 mb-1">
                            {isDistributionMode ? 'Ya has usado todos tus puntos hoy' : 'Ya has votado hoy'}
                          </p>
                          <p className="text-xs text-yellow-700 dark:text-yellow-400 mb-3">
                            {isDistributionMode ? `Recibirás más puntos en ${nextVoteTime}` : `Podrás votar de nuevo en ${nextVoteTime}`}
                          </p>

                          {/* Share Section - Only show if user hasn't shared today */}
                          {!hasShared && (
                            <div className="mt-3 pt-3 border-t border-yellow-300 dark:border-yellow-700">
                              <p className="text-sm font-bold text-yellow-900 dark:text-yellow-200 mb-2">
                                🎁 ¡Comparte y obtén {selectedPoll.poll_points_config?.daily_share_bonus || 10} puntos extra!
                              </p>
                              <div className="flex gap-2 justify-center">
                                {SHOW_FACEBOOK_AND_WHATSAPP && (
                                <button
                                  onClick={() => handleShare('facebook')}
                                  disabled={sharing}
                                  className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg transition disabled:opacity-50"
                                  title="Compartir en Facebook"
                                >
                                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                  </svg>
                                </button>
                                )}
                                <button
                                  onClick={() => handleShare('twitter')}
                                  disabled={sharing}
                                  className="bg-black hover:bg-gray-800 text-white p-2 rounded-lg transition disabled:opacity-50"
                                  title="Compartir en X"
                                >
                                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                  </svg>
                                </button>
                                {SHOW_FACEBOOK_AND_WHATSAPP && (
                                <button
                                  onClick={() => handleShare('whatsapp')}
                                  disabled={sharing}
                                  className="bg-green-600 hover:bg-green-700 text-white p-2 rounded-lg transition disabled:opacity-50"
                                  title="Compartir en WhatsApp"
                                >
                                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                                  </svg>
                                </button>
                                )}
                                <button
                                  onClick={() => handleShare('telegram')}
                                  disabled={sharing}
                                  className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-lg transition disabled:opacity-50"
                                  title="Compartir en Telegram"
                                >
                                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z"/>
                                  </svg>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-300 text-center">
                          {isDistributionMode ? 'Ya has usado todos tus puntos' : 'Ya has votado en esta encuesta'}
                        </p>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        if (!user) {
                          navigate('/login');
                        } else {
                          setShowResults(false);
                        }
                      }}
                      className="w-full bg-violet-600 text-white py-3 rounded-lg font-bold text-lg hover:bg-violet-700 transition mb-4"
                    >
                      Votar
                    </button>
                  )
                )}

                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Resultados</h3>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-semibold">{results.reduce((sum, r) => sum + r.total_points, 0)}</span> puntos totales
                  </div>
                </div>

                {selectedPoll.poll_options
                  .slice()
                  .sort((a, b) => {
                    const resultA = results.find(r => r.option_id === a.id);
                    const resultB = results.find(r => r.option_id === b.id);
                    return (resultB?.total_points || 0) - (resultA?.total_points || 0);
                  })
                  .map(option => {
                  const percentage = getOptionPercentage(option.id);
                  const optionResult = results.find(r => r.option_id === option.id);
                  const isSaved = option.is_saved || false;

                  return (
                    <div key={option.id} className="space-y-2">
                      <button
                        onClick={() => handleShowVoters(option)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition cursor-pointer"
                      >
                        {SHOW_CONTESTANT_PHOTOS && option.contestants?.photo_url && (
                          <img
                            src={option.contestants.photo_url}
                            alt={option.text || ''}
                            className={`w-10 h-10 rounded-full object-cover ${
                              isSaved ? 'opacity-60' : ''
                            }`}
                          />
                        )}
                        <div className="flex-1">
                          <div className="flex justify-between items-center mb-1">
                            <div className="flex items-center gap-2">
                              <span className={`font-medium ${
                                isSaved
                                  ? 'text-emerald-800 dark:text-emerald-300'
                                  : 'text-gray-900 dark:text-white'
                              }`}>
                                {option.contestants?.full_name || option.text}
                              </span>
                              {isSaved && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 dark:bg-emerald-800 text-emerald-800 dark:text-emerald-200 whitespace-nowrap">
                                  ✓ Salvado
                                </span>
                              )}
                            </div>
                            <span className="text-sm font-bold text-gray-700 dark:text-gray-200">{percentage.toFixed(1)}%</span>
                          </div>

                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                            <div
                              className={`h-3 rounded-full transition-all duration-500 ${
                                isSaved
                                  ? 'bg-emerald-600 dark:bg-emerald-500'
                                  : 'bg-violet-600 dark:bg-violet-500'
                              }`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <div className="flex gap-4 text-xs text-gray-500 dark:text-gray-400 mt-1">
                            <span>{optionResult?.total_points || 0} puntos</span>
                            {option.betrayal ? (
                              <span className="text-green-600 dark:text-green-400 ml-auto">
                                {option.betrayal.inherited_votes.toLocaleString('es-MX')} votos heredados de {option.betrayal.replaced_contestant_name}
                              </span>
                            ) : option.inheritance_note && (
                              <span className="text-green-600 dark:text-green-400 ml-auto">
                                {option.inheritance_note}
                              </span>
                            )}
                          </div>
                        </div>
                      </button>

                      {/* Fraud Badge - Outside button to prevent interference */}
                      {fraudReports[option.id] && fraudReports[option.id].length > 0 && (
                        <div className="px-3">
                          <FraudBadge
                            reports={fraudReports[option.id]}
                            contestantName={option.contestants?.full_name || option.text || ''}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Ver todos los votos - Solo en modo resultados */}
            {showResults && results.length > 0 && (
              <div className="mt-6 text-center">
                <button
                  onClick={handleShowAllVoters}
                  className="text-violet-600 dark:text-violet-400 hover:underline font-medium"
                >
                  Ver todos los votos
                </button>
              </div>
            )}
          </div>
        )}

        {/* Small Banner Ad - After Poll (only in encuesta tab) */}
        {activeTab === 'encuesta' && <SmallBannerAd className="mt-6" />}

        {/* Transmisión en vivo */}
        {streamEmbeds.length > 0 && (
        <div id="live-stream" className="mt-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <div className="bg-gradient-to-r from-fuchsia-600 to-indigo-600 dark:from-fuchsia-700 dark:to-indigo-700 px-6 py-4">
            <div className="flex items-center justify-center gap-2">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
              </span>
              <h3 className="text-xl font-bold text-white text-center">
                📺 Ve la transmisión en VIVO
              </h3>
            </div>
          </div>
          {streamEmbeds.length > 1 && (
          <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-2 pt-2">
            <div className="flex gap-1 sm:gap-2">
              {streamEmbeds.map((embed) => (
                <button
                  key={embed.id}
                  type="button"
                  onClick={() => setActiveStreamId(embed.id)}
                  className={`flex-1 min-w-0 px-2 sm:px-4 py-3 rounded-t-lg font-semibold transition-all text-sm ${
                    activeStreamId === embed.id
                      ? 'bg-white dark:bg-gray-800 text-violet-600 dark:text-violet-400 shadow-sm border border-b-0 border-gray-200 dark:border-gray-700 -mb-px'
                      : 'bg-transparent text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                  }`}
                >
                  {embed.name}
                </button>
              ))}
            </div>
          </div>
          )}
          <div className="p-4 pb-5">
            <div className="relative w-full overflow-visible" style={{ paddingBottom: '62%' }}>
              {streamEmbeds.map((embed) =>
                activeStreamId === embed.id ? (
                  <iframe
                    key={embed.id}
                    className="absolute top-0 left-0 w-full h-full rounded-lg"
                    src={embed.embed_url}
                    title={`${embed.name} en vivo`}
                    frameBorder="0"
                    allow="fullscreen; autoplay"
                    allowFullScreen
                  />
                ) : null
              )}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center mt-3">
              ¿No sirve alguna transmisión? Repórtala a La Granja Vota
            </p>
            <a
              href="https://t.me/lacasavota"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 flex items-center justify-center gap-2 w-full sm:w-auto mx-auto bg-[#0088cc] hover:bg-[#0077b5] text-white font-semibold py-2.5 px-4 rounded-lg transition"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
              </svg>
              Telegram
            </a>
          </div>
        </div>
        )}

        {/* Share Section - Only show if daily_reset is enabled and user hasn't shared (only in encuesta tab) */}
        {activeTab === 'encuesta' && user && selectedPoll?.poll_points_config?.daily_reset && !hasShared && (
          <div className="bg-gradient-to-r from-green-600 to-teal-600 dark:from-green-700 dark:to-teal-700 rounded-lg shadow-lg p-6 mt-6 text-white">
            <div className="text-center">
              <div className="bg-white/20 p-3 rounded-full inline-block mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">🎁 ¡Gana {selectedPoll.poll_points_config?.daily_share_bonus || 10} puntos extra!</h3>
              <p className="text-sm text-white/90 mb-4">
                Comparte esta encuesta y obtén puntos adicionales para votar
              </p>
              <div className="flex gap-2 justify-center flex-wrap">
                {SHOW_FACEBOOK_AND_WHATSAPP && (
                <button
                  onClick={() => handleShare('facebook')}
                  disabled={sharing}
                  className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-4 py-2 rounded-lg transition disabled:opacity-50 flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Facebook
                </button>
                )}
                <button
                  onClick={() => handleShare('twitter')}
                  disabled={sharing}
                  className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-4 py-2 rounded-lg transition disabled:opacity-50 flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                  X
                </button>
                {SHOW_FACEBOOK_AND_WHATSAPP && (
                <button
                  onClick={() => handleShare('whatsapp')}
                  disabled={sharing}
                  className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-4 py-2 rounded-lg transition disabled:opacity-50 flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  WhatsApp
                </button>
                )}
                <button
                  onClick={() => handleShare('telegram')}
                  disabled={sharing}
                  className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-4 py-2 rounded-lg transition disabled:opacity-50 flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z"/>
                  </svg>
                  Telegram
                </button>
              </div>
            </div>
          </div>
        )}

        {/* PWA Install Section */}
        {!isInstalled && canInstall && (
          <div className="bg-gradient-to-r from-violet-600 to-fuchsia-600 dark:from-violet-700 dark:to-fuchsia-700 rounded-lg shadow-lg p-6 mt-6 text-white">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 p-3 rounded-full">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-1">Instala esta aplicación</h3>
                  <p className="text-sm text-white/90">
                    Accede más rápido y recibe notificaciones
                  </p>
                </div>
              </div>
              <button
                onClick={installPWA}
                className="bg-white text-violet-600 font-bold py-3 px-6 rounded-lg hover:bg-gray-100 transition whitespace-nowrap"
              >
                Instalar Ahora
              </button>
            </div>
          </div>
        )}
        </>
        )}

        {/* Social Media Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mt-6">
          {/* Telegram Group */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3 text-center">
              📢 Únete al grupo de Telegram
            </h3>
            <a
              href="https://t.me/+RWV06a5uwD8zZmMx"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-4 rounded-lg transition text-center"
            >
              <div className="flex items-center justify-center gap-2">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z"/>
                </svg>
                Unirse al grupo
              </div>
            </a>
          </div>

          {/* Social Media Links */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 text-center">
              Sigue nuestras redes sociales
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {/* Instagram */}
              <a
                href="https://www.instagram.com/lacasavota/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 hover:from-purple-700 hover:via-pink-600 hover:to-orange-500 text-white font-semibold py-3 px-4 rounded-lg transition"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                Instagram
              </a>

              {SHOW_FACEBOOK_AND_WHATSAPP && (
              <a
                href="https://www.facebook.com/profile.php?id=61578807464273"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Facebook
              </a>
              )}

              {/* X (Twitter) */}
              <a
                href="https://x.com/lacasavota"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-black hover:bg-gray-800 text-white font-semibold py-3 px-4 rounded-lg transition"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                X
              </a>

              {/* TikTok */}
              <a
                href="https://www.tiktok.com/@realitymxclips"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-black hover:bg-gray-800 text-white font-semibold py-3 px-4 rounded-lg transition"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                </svg>
                TikTok
              </a>
            </div>
          </div>
        </div>

        {/* Banner Ad - After Social Media Section */}
        <BannerAd className="mt-6" />

      </main>

      {/* Footer */}
      <footer className="bg-gray-100 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-12">
        <div className="container mx-auto px-4 py-8 max-w-2xl">
          <div className="text-center space-y-4">
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">lacasavota.com</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 font-semibold">
                ESTA ENCUESTA NO ES OFICIAL
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                Encuesta hecha por fans para fans
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-2 border-t border-gray-300 dark:border-gray-600 pt-2">
                TV Azteca® es marca registrada. Este sitio NO está afiliado con TV Azteca
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <a
                href="/terminos"
                className="text-violet-600 dark:text-violet-400 hover:underline"
              >
                Términos y Condiciones
              </a>
              <span className="text-gray-400">•</span>
              <a
                href="/privacidad"
                className="text-violet-600 dark:text-violet-400 hover:underline"
              >
                Política de Privacidad
              </a>
            </div>

            <div className="text-xs text-gray-500 dark:text-gray-500">
              <p>© {new Date().getFullYear()} La Granja Vota. Todos los derechos reservados.</p>
              <p className="mt-1">
                Contacto: <a href="mailto:lagranjavota@gmail.com" className="text-violet-600 dark:text-violet-400 hover:underline">lagranjavota@gmail.com</a>
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Voters Modal */}
      {showVotersModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-violet-600 dark:bg-gray-700 text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {SHOW_CONTESTANT_PHOTOS && selectedOptionForModal?.contestants?.photo_url && (
                  <img
                    src={selectedOptionForModal.contestants.photo_url}
                    alt={selectedOptionForModal.contestants?.full_name || selectedOptionForModal.text || ''}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                )}
                <div>
                  <h3 className="font-bold text-lg">
                    {selectedOptionForModal
                      ? (selectedOptionForModal.contestants?.full_name || selectedOptionForModal.text)
                      : 'Todos los votos'}
                  </h3>
                  <p className="text-sm opacity-90">
                    {selectedOptionVoters?.total_points || 0} puntos
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowVotersModal(false)}
                className="text-white hover:bg-white/20 rounded-full p-2 transition"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Voters List */}
            <div
              ref={votersListRef}
              onScroll={handleVotersScroll}
              className="p-4 overflow-y-auto max-h-[calc(80vh-120px)]"
            >
              {!selectedOptionVoters ? (
                /* Loading state */
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
                  <p className="mt-4 text-gray-600 dark:text-gray-400">Cargando votantes...</p>
                </div>
              ) : selectedOptionVoters.voters.length > 0 ? (
                <div className="space-y-3">
                  {selectedOptionVoters.voters.map((voter, index) => (
                    <div
                      key={`${voter.user_id}-${index}`}
                      className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                    >
                      {voter.avatar_url ? (
                        <img
                          src={voter.avatar_url}
                          alt={voter.display_name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-violet-500 flex items-center justify-center text-white font-bold">
                          {voter.display_name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-white">
                          {voter.display_name}
                        </p>
                        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                          <span>
                            {voter.points} {voter.points === 1 ? 'punto' : 'puntos'}
                          </span>
                          <span>•</span>
                          <span>{getRelativeTime(voter.created_at)}</span>
                          {!selectedOptionForModal && (voter as any).option_name && (
                            <>
                              <span>•</span>
                              <span className="text-violet-600 dark:text-violet-400 font-medium">
                                {(voter as any).option_name}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Loading more indicator */}
                  {loadingMoreVoters && (
                    <div className="flex justify-center py-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
                    </div>
                  )}

                  {/* End of list message */}
                  {!hasMoreVoters && selectedOptionVoters.voters.length > 0 && (
                    <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
                      <p>No hay más votantes</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <p>No hay votos aún</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Install Prompt Modal */}
      {showInstallPrompt && !isInstalled && canInstall && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full overflow-hidden animate-slideUp">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white p-6 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-3">
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold">¡Instala La Granja Vota!</h3>
              <p className="text-sm text-white/90 mt-2">
                Accede más rápido desde tu pantalla de inicio
              </p>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="bg-violet-100 dark:bg-violet-900/30 p-2 rounded-full">
                    <svg className="w-5 h-5 text-violet-600 dark:text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">Acceso rápido</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Abre la app con un solo toque</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-fuchsia-100 dark:bg-fuchsia-900/30 p-2 rounded-full">
                    <svg className="w-5 h-5 text-fuchsia-600 dark:text-fuchsia-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">Notificaciones</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Mantente al día con las votaciones</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-green-100 dark:bg-green-900/30 p-2 rounded-full">
                    <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">Sin anuncios</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Experiencia nativa sin interrupciones</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={dismissInstallPrompt}
                  className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 font-semibold py-3 px-4 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition"
                >
                  Ahora no
                </button>
                <button
                  onClick={installPWA}
                  className="flex-1 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-bold py-3 px-4 rounded-lg hover:from-violet-700 hover:to-fuchsia-700 transition"
                >
                  Instalar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
